import React, {
  Fragment,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react'
import { Link } from 'gatsby'

import Hamburger from '../../UI/Hamburger/Hamburger'
import * as styles from './Navigation.module.scss'

import AboutLinkList from './LinksLists/AboutLinkList'
import ServicesLinkList from './LinksLists/ServicesLinkList'
import SocialLinkList from './LinksLists/SocialLinkList'

// adding in workspaces services height will be 456
const Navigation = ({ mobileNavHandler, activeMobileNav }) => {
  const [dimensions, setDimensions] = useState({
    about: { width: 280, height: 282, x: 194, arrowX: 140 }, //height 282 for additional link
    services: { width: 800, height: 292, x: 28, arrowX: 300 },
    socialToggle: { width: 340, height: 446, x: 488, arrowX: 40 },
  })
  const [popOverState, setPopOverState] = useState({
    about: false,
    services: false,
    socialToggle: false,
  })
  const focusables = useRef(null)
  const sectionEls = useRef([])
  const navLinkEls = useRef([])
  const headerEl = useRef()
  const popoverEl = useRef()
  const contentEl = useRef()
  const arrowEl = useRef()
  const backgroundEl = useRef()
  const popoverLeft = useRef({
    val: 0,
  })

  //get and set section and links var
  const getPopOverLeftVal = () => {
    popoverLeft.current.val = popoverEl.current.getBoundingClientRect().x
  }

  useEffect(() => {
    getPopOverLeftVal()
  }, [popoverEl])

  useEffect(() => {
    arrowEl.current.style.transform = `
            translateX(${dimensions.about.arrowX}px)
            rotate(45deg)`
  }, [arrowEl, dimensions.about.arrowX])

  //set arrowX for each Link
  useEffect(() => {
    let settingDimensions = true
    const spacing = {
      about: 0,
      services: 0,
      socialToggle: 0,
    }
    if (settingDimensions) {
      navLinkEls.current.forEach((navLink) => {
        let section = navLink.getAttribute('data-nav')
        let rect = navLink.getBoundingClientRect()

        setDimensions((prevState) => {
          return {
            ...prevState,
            [section]: {
              ...prevState[section],
              arrowX:
                rect.left +
                rect.width / 2 -
                (popoverLeft.current.val - spacing[section]),
            },
          }
        })
      })
    }
    return () => {
      settingDimensions = false
    }
  }, [navLinkEls, popoverLeft.current.val])

  const keyDownHandler = useCallback(
    (e) => {
      if (!focusables.current) return
      const numRef = {
        about: 0,
        services: 1,
        socialToggle: 2,
      }
      if (e.keyCode === 9) {
        if (
          e.shiftKey &&
          document.activeElement === focusables.current.children[0]
        ) {
          e.preventDefault()
          navLinkEls.current[numRef[focusables.current.parent]].focus()
          window.removeEventListener('keydown', keyDownHandler)

          focusables.current = null

          return
        } else if (
          !e.shiftKey &&
          document.activeElement ===
            focusables.current.children[focusables.current.children.length - 1]
        ) {
          e.preventDefault()
          focusables.current.children[
            focusables.current.children.length - 1
          ].focus()
        }
      }
    },
    [focusables, navLinkEls]
  )

  const closePopoverHandler = useCallback(() => {
    setPopOverState((prevState) => {
      return {
        about: false,
        services: false,
        socialToggle: false,
      }
    })
    focusables.current = null
  }, [setPopOverState, focusables])

  const blurOnSocialTabbing = useCallback(
    (e) => {
      if (e.keyCode === 9) {
        closePopoverHandler()
      }
    },
    [closePopoverHandler]
  )

  useEffect(() => {
    let socialButton = navLinkEls.current[2]
    socialButton.addEventListener('keydown', blurOnSocialTabbing)
    return () => {
      socialButton.removeEventListener('keydown', blurOnSocialTabbing)
    }
  }, [navLinkEls, blurOnSocialTabbing])

  useEffect(() => {
    if (
      Object.keys(popOverState).some((k) => {
        return popOverState[k] === true
      })
    ) {
      window.addEventListener('keydown', keyDownHandler)
    }
    return () => {
      window.removeEventListener('keydown', keyDownHandler)
    }
  }, [focusables, popOverState, keyDownHandler])

  const showSection = useCallback(
    (section) => {
      setPopOverState((prevState) => {
        return {
          about: false,
          services: false,
          socialToggle: false,
          [section]: true,
        }
      })

      //   Position arrow
      arrowEl.current.style.transform = `
            translateX(${dimensions[section].arrowX}px)
            rotate(45deg)`

      //   Resize and position background
      backgroundEl.current.style.transform = `
            translateX(${dimensions[section].x}px)
            scaleX(${dimensions[section].width / dimensions['about'].width})
            scaleY(${dimensions[section].height / dimensions['about'].height})
          `

      //   Resize and position content
      contentEl.current.style.width = dimensions[section].width + 'px'
      contentEl.current.style.height = dimensions[section].height + 'px'
      contentEl.current.style.transform = `translateX(${dimensions[section].x}px)`
    },
    [contentEl, backgroundEl, arrowEl, dimensions]
  )

  const openPopoverHandler = useCallback(
    (e) => {
      let targetPopover = e.target.getAttribute('data-nav')
      if (popoverLeft.current.val === 0) {
        getPopOverLeftVal()
      }
      if (targetPopover) {
        showSection(targetPopover)
      }
    },
    [showSection]
  )

  const onKeyPressHandler = (e) => {
    let targetPopover = e.target.getAttribute('data-nav')
    //if enter, change state, set focusables & set focus on first in list
    if (e.charCode === 13) {
      setPopOverState((prevState) => {
        return {
          about: false,
          services: false,
          socialToggle: false,
          [targetPopover]: true,
        }
      })
      const numRef = {
        about: 0,
        services: 1,
        socialToggle: 2,
      }
      sectionEls.current[numRef[targetPopover]].querySelector('a').focus()
      focusables.current = {
        parent: targetPopover,
        children:
          sectionEls.current[numRef[targetPopover]].querySelectorAll(
            'a, input'
          ),
      }
    }
  }

  const onBlurHandler = (e) => {
    if (
      e.relatedTarget &&
      e.relatedTarget.nodeType === Node.ELEMENT_NODE &&
      !e.currentTarget?.contains(e.relatedTarget)
    ) {
      closePopoverHandler()
    }
  }

  return (
    <Fragment>
      <nav
        //  role="button"
        // tabIndex={0}
        className={styles.header__navbarWrap}
        onMouseLeave={onBlurHandler}
        onBlur={onBlurHandler}
      >
        <article className={`${styles.header__navbar} navbar`}>
          <section
            role="tablist"
            className={`${styles.header__navLinks}`}
            ref={headerEl}
          >
            <button
            aria-controls="about-menu"
              role="tab"
              aria-label="Meet Precise Dropdown"
              aria-haspopup="true"
              aria-selected={popOverState.about}
              aria-expanded={popOverState.about}
              ref={(el) => (navLinkEls.current[0] = el)}
              id={'about'}
              className={`${popOverState.about ? 'active' : ''} popover_link`}
              data-nav="about"
              onKeyPress={onKeyPressHandler}
              onMouseOver={openPopoverHandler}
              onFocus={openPopoverHandler}
            >
              Meet Precise
            </button>
            <button
              aria-controls="services-menu"
              role="tab"
              aria-label="Our Capabilities Dropdown"
              aria-haspopup="true"
              aria-expanded={popOverState.services}
              aria-selected={popOverState.services}
              ref={(el) => (navLinkEls.current[1] = el)}
              id={'services'}
              className={`${
                popOverState.services ? 'active' : ''
              } popover_link`}
              data-nav="services"
              onMouseOver={openPopoverHandler}
              onFocus={openPopoverHandler}
              onKeyPress={onKeyPressHandler}
            >
              Our Capabilities
            </button>
            <button
              tabIndex="-1"
              onMouseOver={closePopoverHandler}
              onFocus={closePopoverHandler}
              aria-label="Careers Link"
            >
              <Link to="/careers">Careers</Link>
            </button>
            <Hamburger
              aria-controls="socialToggle-menu"
              role="tab"
              aria-label="social links dropdown"
              aria-haspopup="true"
              aria-expanded={popOverState.socialToggle}
              aria-selected={popOverState.socialToggle}
              ref={(el) => (navLinkEls.current[2] = el)}
              id={'socialToggle'}
              classes={[
                `header__socialToggle`,
                `popover_link`,
                `${popOverState.socialToggle ? 'active' : ''}`,
              ]}
              data-nav={'socialToggle'}
              onMouseOver={openPopoverHandler}
              onFocus={openPopoverHandler}
              onKeyPress={onKeyPressHandler}
            />
            <div
              ref={popoverEl}
              className={`${
                Object.keys(popOverState).some((k) => {
                  return popOverState[k] === true
                })
                  ? 'open'
                  : ''
              } ${Object.keys(popOverState).filter((id) => {
                return popOverState[id]
              })} popover`}
            >
              <div ref={contentEl} className={`content`}>
                <div
                  aria-labelledby="about"
                  hidden={!popOverState.about}
                  aria-hidden={!popOverState.about}
                  id={'about-menu'}
                  ref={(el) => (sectionEls.current[0] = el)}
                  className={`section section_about ${
                    popOverState.about ? 'active' : ''
                  }`}
                >
                  <AboutLinkList />
                </div>
                <div
                  aria-labelledby="services"
                  hidden={!popOverState.services}
                  aria-hidden={!popOverState.services}
                  ref={(el) => (sectionEls.current[1] = el)}
                  id={'services-menu'}
                  className={`section section_services ${
                    popOverState.services ? 'active' : ''
                  }`}
                >
                  <ServicesLinkList />
                </div>
                <div
                  aria-labelledby="socialToggle"
                  hidden={!popOverState.socialToggle}
                  aria-hidden={!popOverState.socialToggle}
                  ref={(el) => (sectionEls.current[2] = el)}
                  id={'socialToggle-menu'}
                  className={`section section_socialToggle ${
                    popOverState.socialToggle ? 'active' : ''
                  }`}
                >
                  <SocialLinkList />
                </div>
              </div>
              <div ref={backgroundEl} className="background"></div>
              <div ref={arrowEl} className="arrow"></div>
            </div>
          </section>
          <Hamburger
            aria-label="mobile navigation toggle"
            id="mobileNav__toggle"
            classes={[`${styles.header__mobileToggle}`]}
            onClick={mobileNavHandler}
            onKeyPress={mobileNavHandler}
          />
        </article>
      </nav>
    </Fragment>
  )
}

export default Navigation
